import { useDialog } from '@/common/dialog/DialogContext';
import { EntityForm } from '@/common/entityFramework/entityForm/EntityForm';
import { EntityFormDialog } from '@/common/entityFramework/entityForm/EntityFormDialog';
import { useEntityMgr } from '@/common/entityFramework/entityView/EntityMgrContext';
import { EntityFormMode } from '@/common/entityFramework/enumerations/EntityFormMode';
import { useForm } from '@/common/form/FormContext';
import { DynamicFormField } from '@/common/form/formFields/DynamicFormField';
import { withConditionalRender } from '@/common/form/higherOrderComponents/withConditionalRender';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { TrxColFormField } from './formFields/TrxColFormField';

const LIST_OPERATORS = ['IS_ONE_OF', 'IS_NOT_ONE_OF'];
const NO_VALUE_OPERATORS = ['EXISTS', 'DOES_NOT_EXIST'];

const ValueFormField = withConditionalRender(
  (formValues) => ![...LIST_OPERATORS, ...NO_VALUE_OPERATORS].includes(formValues.operator),
)(DynamicFormField);
const IncListSelect = withConditionalRender((formValues) => LIST_OPERATORS.includes(formValues.operator))(
  // The list picker is scoped to the Filter; until a list is chosen the user can create one in place
  ({ fieldDef, filterId }) => {
    const module = useModuleFromRoute();
    const { formValues, assignValue } = useForm();
    const { openDialog } = useDialog();

    const openNewListDialog = () => {
      openDialog(EntityFormDialog, {
        title: 'New Inclusion List',
        module,
        entity: 'INC_LIST',
        mode: EntityFormMode.ADD,
        initialValues: { filterId },
        maxWidth: 'md',
      }).then((saved) => {
        if (!saved) return;
        assignValue('incListId', saved.id);
        assignValue('incListCode', saved.code);
        assignValue('incListName', saved.name);
      });
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DynamicFormField fieldDef={fieldDef} />
        </Box>
        {!formValues.incListId && (
          <Tooltip title="Create a new inclusion list">
            <IconButton size="small" onClick={openNewListDialog}>
              <FontAwesomeIcon icon={fas.faPlus} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  },
);

// Operator decides whether a Condition is evaluated against a literal Value or an Inclusion List.
// Transaction columns and the owning Filter are resolved server-side from the rule (add) or the condition (edit).
export const IncCondForm = (formProps) => {
  const rsrc = useRsrc();
  const module = useModuleFromRoute();
  const { entityObj } = useEntityMgr();

  const incRuleId = formProps.initialValues?.incRuleId ?? entityObj?.incRuleId;
  const incCondId = incRuleId ? null : formProps.entityId;
  const [formData, setFormData] = useState();
  useEffect(() => {
    if (!incRuleId && !incCondId) {
      setFormData({});
      return;
    }
    rsrc.data(module, 'INC_COND_FORM_DATA_PLUGIN', { incRuleId, incCondId }).then(setFormData);
  }, [incRuleId, incCondId]);

  if (!formData) {
    return null;
  }

  const { trxCols = [], filterId } = formData;

  const augFieldDefs = (fieldToFieldDef) => {
    if (trxCols.length > 0) {
      fieldToFieldDef.trxCol.component = (
        <TrxColFormField key="trxCol" trxCols={trxCols} fieldDef={fieldToFieldDef.trxCol} />
      );
    }
    fieldToFieldDef.value.component = <ValueFormField key="value" fieldDef={fieldToFieldDef.value} />;
    if (filterId) {
      fieldToFieldDef.incListId.expr = ExprUtils.whereParams('filterId', filterId);
    }
    fieldToFieldDef.incListId.component = (
      <IncListSelect key="incListId" fieldDef={fieldToFieldDef.incListId} filterId={filterId} />
    );
  };

  return <EntityForm augFieldDefs={augFieldDefs} {...formProps} />;
};
